import {
  createUserWithEmailAndPassword,
  updateProfile,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updatePassword as firebaseUpdatePassword,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, updateDoc, collection, query, where, getDocs, addDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { User } from '@/types/users';
import { loadMockData } from '@/mock';
import type { WorkspaceReference } from '@/types/common';

export const updateUserPassword = async (userId: string, newPassword: string) => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, { password: newPassword });
};

function mapDocToUser(docId: string, data: Record<string, unknown>): User {
  return {
    id: docId,
    uid: data.uid as string,
    email: data.email as string,
    displayName: data.displayName as string,
    username: (data.username as string) || (data.email as string).toString().split('@')[0],
    status: (data.status as User['status']) || 'active',
    createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    lastLogin: (data.lastLogin as { toDate?: () => Date })?.toDate?.(),
    contacts: (data.contacts as User['contacts']) || [],
    workspace: data.workspace
      ? {
          id: (data.workspace as WorkspaceReference).id,
          name: (data.workspace as WorkspaceReference).name,
          slug: (data.workspace as WorkspaceReference).slug,
        }
      : undefined,
    roles: (data.roles as User['roles']) || [],
    language: (data.language as User['language']) || 'en',
    currency: (data.currency as User['currency']) || 'USD',
    profilePicture: data.profilePicture as User['profilePicture'],
    phoneNumber: data.phoneNumber as string | undefined,
    activityRecords: data.activityRecords as User['activityRecords'],
  };
}

const generateUsername = (displayName: string): string =>
  displayName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .substring(0, 20);

/** Create Firestore user document (no workspace) - used after signup. Optional pendingWorkspace pre-fills create workspace. */
export const createUserDocument = async (
  uid: string,
  email: string,
  displayName: string,
  phoneNumber?: string,
  pendingWorkspace?: { apartmentName?: string; country?: string; region?: string }
): Promise<string> => {
  const usersRef = collection(db, 'users');
  const userData: Record<string, unknown> = {
    uid,
    email,
    displayName,
    username: generateUsername(displayName),
    status: 'active',
    createdAt: new Date(),
    contacts: [],
    roles: ['resident'],
    language: 'en',
    currency: 'USD',
    phoneNumber: phoneNumber ?? null,
    invited: false,
    accepted: true,
  };
  if (pendingWorkspace && (pendingWorkspace.apartmentName ?? pendingWorkspace.country ?? pendingWorkspace.region)) {
    userData.pendingWorkspace = pendingWorkspace;
  }
  const docRef = await addDoc(usersRef, userData);
  return docRef.id;
};

/** Create a second user document for the same person in a workspace (e.g. admin of new workspace) */
export const createUserDocumentForWorkspace = async (
  uid: string,
  email: string,
  displayName: string,
  workspace: WorkspaceReference,
  role: User['roles'][0] = 'administrator'
): Promise<string> => {
  const usersRef = collection(db, 'users');
  const userData = {
    uid,
    email,
    displayName,
    username: generateUsername(displayName),
    status: 'active',
    createdAt: new Date(),
    contacts: [],
    roles: [role],
    workspace: { id: workspace.id, name: workspace.name, slug: workspace.slug },
    language: workspace.language || 'en',
    currency: 'USD',
    invited: false,
    accepted: true,
  };
  const docRef = await addDoc(usersRef, userData);
  return docRef.id;
};

/** Sign up with email/password and create Firestore user (no workspace); user then selects or creates workspace */
export const signUp = async (
  email: string,
  password: string,
  displayName: string,
  phoneNumber?: string
): Promise<FirebaseUser> => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(userCredential.user, { displayName });
  await createUserDocument(userCredential.user.uid, email, displayName, phoneNumber);
  return userCredential.user;
};

const PENDING_REGISTRATIONS = 'pendingRegistrations';
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

const AUTH_LOG_PREFIX = '[Convive Auth]';

/** Partially mask email for browser console logs (admin app debugging). */
function maskEmailForLog(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return '(invalid)';
  const safeLocal = !local || local.length <= 1 ? '*' : `${local[0]}***`;
  return `${safeLocal}@${domain}`;
}

export interface PendingRegistration {
  email: string;
  fullName: string;
  apartmentName?: string;
  country?: string;
  region?: string;
  createdAt: unknown;
}

/** Save pending registration to Firestore (for email link flow). */
export const savePendingRegistration = async (
  email: string,
  fullName: string,
  apartmentName?: string,
  country?: string,
  region?: string
): Promise<void> => {
  const ref = collection(db, PENDING_REGISTRATIONS);
  await addDoc(ref, {
    email,
    fullName,
    apartmentName: apartmentName ?? null,
    country: country ?? null,
    region: region ?? null,
    createdAt: new Date(),
  });
};

/** Get and delete pending registration by email. Returns null if not found. */
export const consumePendingRegistration = async (email: string): Promise<PendingRegistration | null> => {
  const ref = collection(db, PENDING_REGISTRATIONS);
  const q = query(ref, where('email', '==', email));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const docSnap = snapshot.docs[0];
  const data = docSnap.data();
  await deleteDoc(doc(db, PENDING_REGISTRATIONS, docSnap.id));
  return {
    email: data.email,
    fullName: data.fullName,
    apartmentName: data.apartmentName ?? undefined,
    country: data.country ?? undefined,
    region: data.region ?? undefined,
    createdAt: data.createdAt,
  };
};

/** Send sign-in link to email (passwordless registration). Call savePendingRegistration first. */
export const sendSignInLinkForRegistration = async (email: string): Promise<void> => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const actionCodeSettings = {
    url: `${baseUrl}/auth/complete-signup`,
    handleCodeInApp: true,
  };
  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    console.info(`${AUTH_LOG_PREFIX} sendSignInLinkToEmail completed successfully`, {
      email: maskEmailForLog(email),
      continueUrl: actionCodeSettings.url,
    });
  } catch (e) {
    const err = e as { code?: string; message?: string };
    console.error(`${AUTH_LOG_PREFIX} sendSignInLinkToEmail failed`, {
      email: maskEmailForLog(email),
      continueUrl: actionCodeSettings.url,
      code: err.code,
      message: err.message,
    });
    throw e;
  }
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
  }
};

export const getStoredEmailForSignIn = (): string | null => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
};

export const clearStoredEmailForSignIn = (): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
  }
};

export const isSignInWithEmailLinkAuth = (url: string): boolean => isSignInWithEmailLink(auth, url);

/**
 * Complete sign-in from email link, create Firestore user (no workspace), then clear stored email.
 * Pending registration details (apartmentName, country, region) are stored on the user doc for pre-filling create workspace.
 */
export const completeSignInWithEmailLink = async (email: string, fullUrl: string): Promise<FirebaseUser> => {
  const userCredential = await signInWithEmailLink(auth, email, fullUrl);
  clearStoredEmailForSignIn();
  const pending = await consumePendingRegistration(email);
  if (pending) {
    await updateProfile(userCredential.user, { displayName: pending.fullName });
    await createUserDocument(userCredential.user.uid, email, pending.fullName, undefined, {
      apartmentName: pending.apartmentName,
      country: pending.country,
      region: pending.region,
    });
  }
  return userCredential.user;
};

/** Dedupe concurrent completions for the same oobCode (React Strict Mode runs effects twice in dev). */
const emailLinkCompleteByOob = new Map<string, Promise<FirebaseUser>>();

function getOobCodeFromUrl(fullUrl: string): string | null {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(fullUrl, base).searchParams.get('oobCode');
  } catch {
    return null;
  }
}

/**
 * Use this from the email-link landing page so the same link is not applied twice when
 * effects run twice; all callers await the same in-flight promise.
 */
export const completeSignInWithEmailLinkDeduped = async (email: string, fullUrl: string): Promise<FirebaseUser> => {
  const oob = getOobCodeFromUrl(fullUrl) ?? `url:${fullUrl}`;
  let pending = emailLinkCompleteByOob.get(oob);
  if (!pending) {
    pending = completeSignInWithEmailLink(email, fullUrl).finally(() => {
      emailLinkCompleteByOob.delete(oob);
    });
    emailLinkCompleteByOob.set(oob, pending);
  }
  return pending;
};

/** Set password for the currently signed-in user (call after email link sign-in). */
export const setPasswordForCurrentUser = async (newPassword: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('No user signed in');
  await firebaseUpdatePassword(user, newPassword);
};

/**
 * Get user data during login without requiring firestoreUser.
 * This is specifically for the login flow where we don't have the firestoreUser yet.
 */
export const getUserByIdForAuth = async (uid: string): Promise<User | null> => {
  console.log('getUserByIdForAuth called with uid:', uid);
  
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('uid', '==', uid)
    );
    console.log('Querying users collection for uid:', uid);
    const snapshot = await getDocs(q);
    
    console.log('Query result - empty:', snapshot.empty, 'size:', snapshot.size);
    
    if (snapshot.empty) {
      console.log('No user found with uid:', uid);
      // Let's also check if there are any users in the collection at all
      const allUsersSnapshot = await getDocs(usersRef);
      console.log('Total users in collection:', allUsersSnapshot.size);
      if (allUsersSnapshot.size > 0) {
        console.log('Sample user data:', allUsersSnapshot.docs[0].data());
      }
      return null;
    }

    const firstDoc = snapshot.docs[0];
    const data = firstDoc.data();
    console.log('User data found:', { id: firstDoc.id, email: data.email });
    return mapDocToUser(firstDoc.id, data);
  } catch (error) {
    console.error('Error fetching user during auth:', error);
    throw new Error('Failed to fetch user during auth');
  }
}; 

export async function getCurrentUser(): Promise<User | null> {
  // Demo mode check
  if (process.env.NODE_ENV === 'development') {
    console.log('DEMO MODE: Fetching mock current user');
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockUsers = await loadMockData<User[]>('users', 'en');
    const currentUser = mockUsers.find(user => user.id === 'current-user') || null;
    return currentUser;
  }

  // For now, return null in non-demo mode
  // This function would typically check Firebase Auth or other auth state
  return null;
} 